import React, { useEffect, useState } from "react";
import { X, FileText, ShieldCheck, Loader2, Lock, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * W-9 / W-8BEN / W-8BEN-E tax form modal.
 *
 * - TIN (SSN/EIN) is transmitted over TLS and encrypted at rest on the
 *   backend via Fernet. Only last4 is stored in the users row + returned in
 *   any API response.
 * - Required once lifetime earnings cross $600 (IRS threshold for 1099-NEC).
 *   Before that the modal still works for pre-emptive signing.
 * - On success, users.tax_form_complete = true → payouts unblocked.
 */
export default function TaxFormModal({ open, onClose, onSubmitted }) {
  const [status, setStatus] = useState(null);
  const [formType, setFormType] = useState("W9");
  const [form, setForm] = useState({
    legal_name: "",
    business_name: "",
    entity_type: "individual",
    tin: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_postal: "",
    address_country: "US",
    signature: "",
    certifies_accurate: false,
    certifies_us_person: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/creator/tax-form/status").then((r) => {
      setStatus(r.data);
      if (r.data?.form?.form_type) setFormType(r.data.form.form_type);
    }).catch(() => {});
  }, [open]);

  if (!open) return null;

  const spec = status?.supported_forms?.[formType];
  const isW9 = formType === "W9";
  const tinDigits = form.tin.replace(/\D/g, "");
  const tinValid = isW9 ? tinDigits.length === 9 : tinDigits.length >= 5;
  const canSubmit =
    form.legal_name.trim().length >= 3 &&
    tinValid &&
    form.signature.trim().length >= 3 &&
    form.certifies_accurate &&
    (!isW9 || form.certifies_us_person) &&
    form.address_street.trim() &&
    form.address_city.trim() &&
    form.address_postal.trim();

  const submit = async () => {
    if (!canSubmit) { toast.error("Fill in every required field and tick all certifications."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/creator/tax-form", { form_type: formType, ...form });
      toast.success(`${formType} filed — TIN ending ••••${data.form.tin_last4}`);
      onSubmitted?.(data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit tax form";
      toast.error(typeof msg === "string" ? msg : "Could not submit tax form");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tax-form-modal" className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="tv-glass rounded-2xl w-full max-w-2xl border border-white/10 overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-tv-accent" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-bold">🧾 Tax information</div>
              <div className="font-display font-black text-white text-lg tracking-tight">Creator Tax Form</div>
            </div>
          </div>
          <button data-testid="tax-form-close-btn" onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-lg p-3 text-white/75 text-xs">
            <Lock className="w-4 h-4 mt-0.5 shrink-0 text-tv-accent" />
            <div>
              Your TIN is encrypted at rest. Only the last 4 digits are displayed anywhere in the platform.
              Required by IRS rules once lifetime earnings reach <b className="text-white">${status?.threshold_usd?.toFixed?.(0) || "600"}</b>.
              {status?.earnings_to_date_usd !== undefined && <> You've earned ${status.earnings_to_date_usd.toFixed(2)} so far.</>}
            </div>
          </div>

          {status?.submitted && (
            <div data-testid="tax-form-already-filed" className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-300 text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              {status.form.form_type} on file · ending ••••{status.form.tin_last4} · filed {new Date(status.form.submitted_at).toLocaleDateString()}. Re-submit to update.
            </div>
          )}

          {/* Form type picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Which form applies to you?</label>
            <div className="grid sm:grid-cols-3 gap-2 mt-1.5">
              {Object.entries(status?.supported_forms || {}).map(([k, meta]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`tax-form-type-${k}`}
                  onClick={() => setFormType(k)}
                  className={`text-left rounded-lg border p-3 transition ${
                    formType === k ? "border-tv-accent bg-tv-accent/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="font-display font-bold text-white text-sm">{meta.label}</div>
                  <div className="text-[11px] text-white/55 mt-1 leading-snug">{meta.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Legal details */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Legal name (as on tax return) *" testid="tax-legal-name" value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} placeholder="John Q Creator" />
            <Field label="Business name (if different)" testid="tax-business-name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Entity type *</span>
              <select
                data-testid="tax-entity-type"
                value={form.entity_type}
                onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                className="w-full mt-1 bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
              >
                {spec?.entity_types?.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </label>
            <Field
              label={isW9 ? "SSN or EIN * (9 digits)" : "Foreign TIN *"}
              testid="tax-tin"
              value={form.tin}
              onChange={(v) => setForm({ ...form, tin: v.replace(/[^0-9-]/g, "") })}
              placeholder={isW9 ? "123-45-6789 or 12-3456789" : "National tax ID"}
              mono
            />
          </div>

          {/* Address */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1.5">Mailing address *</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Street" testid="tax-address-street" value={form.address_street} onChange={(v) => setForm({ ...form, address_street: v })} />
              <Field label="City" testid="tax-address-city" value={form.address_city} onChange={(v) => setForm({ ...form, address_city: v })} />
              <Field label="State / Province" testid="tax-address-state" value={form.address_state} onChange={(v) => setForm({ ...form, address_state: v })} />
              <Field label="Postal code" testid="tax-address-postal" value={form.address_postal} onChange={(v) => setForm({ ...form, address_postal: v })} />
              {!isW9 && (
                <Field label="Country" testid="tax-address-country" value={form.address_country} onChange={(v) => setForm({ ...form, address_country: v })} />
              )}
            </div>
          </div>

          {/* Certifications */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2.5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="tax-cert-accurate"
                checked={form.certifies_accurate}
                onChange={(e) => setForm({ ...form, certifies_accurate: e.target.checked })}
                className="mt-0.5 accent-tv-accent"
              />
              <span className="text-white/85 text-sm">
                Under penalty of perjury, I certify that the information above is true, correct, and complete.
              </span>
            </label>
            {isW9 && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="tax-cert-us-person"
                  checked={form.certifies_us_person}
                  onChange={(e) => setForm({ ...form, certifies_us_person: e.target.checked })}
                  className="mt-0.5 accent-tv-accent"
                />
                <span className="text-white/85 text-sm">
                  I am a US citizen, resident alien, or US-domiciled entity as defined by the IRS.
                </span>
              </label>
            )}
          </div>

          {/* Signature */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold">✍️ Type your signature (legal name)</label>
            <input
              data-testid="tax-signature"
              value={form.signature}
              onChange={(e) => setForm({ ...form, signature: e.target.value })}
              placeholder="John Q Creator"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-white text-lg font-display italic outline-none focus:border-tv-accent"
            />
          </div>

          {!canSubmit && (
            <div className="flex items-start gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>Complete every required field above, then tick the certifications and type your signature to file.</div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-white/10 flex items-center justify-end gap-3 shrink-0">
          <button data-testid="tax-form-cancel-btn" onClick={onClose} className="text-white/70 hover:text-white font-semibold px-4 py-2 rounded-full">Cancel</button>
          <button
            data-testid="tax-form-submit-btn"
            onClick={submit}
            disabled={!canSubmit || busy}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-2.5 rounded-full disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {busy ? "Filing…" : `File ${formType}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, mono }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}
